import {
  forwardRef,
  useEffect,
  useState,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/components/ui/utils";
import { Button } from "@/components/ui/button";
import { LiveWaveform } from "@/components/ui/live-waveform";

export type VoiceButtonState = "idle" | "recording" | "processing" | "success" | "error";

export interface VoiceButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onError"> {
  /**
   * Current state of the voice button
   * @default "idle"
   */
  state?: VoiceButtonState;

  /**
   * Callback when button is clicked
   */
  onPress?: () => void;

  /**
   * Content to display on the left side (label)
   */
  label?: ReactNode;

  /**
   * Content to display on the right side (e.g. keyboard shortcut)
   */
  trailing?: ReactNode;

  /**
   * Icon to display in center when idle (for icon size buttons)
   */
  icon?: ReactNode;

  /**
   * Custom variant for the button
   * @default "outline"
   */
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";

  /**
   * Size of the button
   * @default "default"
   */
  size?: ComponentProps<typeof Button>["size"];

  /**
   * Custom className for the button
   */
  className?: string;

  /**
   * Custom className for the waveform container
   */
  waveformClassName?: string;

  /**
   * Duration in ms to show success/error states
   * @default 1500
   */
  feedbackDuration?: number;

  /**
   * Disable the button
   */
  disabled?: boolean;
}

export const VoiceButton = forwardRef<HTMLButtonElement, VoiceButtonProps>(
  (
    {
      state = "idle",
      onPress,
      label,
      trailing,
      icon,
      variant = "outline",
      size = "default",
      className,
      waveformClassName,
      feedbackDuration = 1500,
      disabled,
      onClick,
      ...props
    },
    ref,
  ) => {
    const [feedback, setFeedback] = useState({
      state,
      duration: feedbackDuration,
      visible: state === "success" || state === "error",
    });
    if (feedback.state !== state || feedback.duration !== feedbackDuration) {
      setFeedback({
        state,
        duration: feedbackDuration,
        visible: state === "success" || state === "error",
      });
    }
    const showFeedback = feedback.visible;

    useEffect(() => {
      if (state === "success" || state === "error") {
        const timeout = setTimeout(() => {
          setFeedback((current) =>
            current.state === state && current.duration === feedbackDuration
              ? { ...current, visible: false }
              : current,
          );
        }, feedbackDuration);
        return () => clearTimeout(timeout);
      }
    }, [state, feedbackDuration]);

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      onPress?.();
    };

    const isRecording = state === "recording";
    const isProcessing = state === "processing";
    const isSuccess = state === "success";
    const isError = state === "error";

    const isDisabled = disabled || isProcessing;
    const isIconButton = size === "icon" || size === "mobileTouchIcon" || size === "touchIcon";
    const shouldShowWaveform = isRecording || isProcessing || showFeedback;
    const shouldShowTrailing = !shouldShowWaveform && trailing;

    return (
      <Button
        ref={ref}
        type="button"
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={isDisabled}
        className={cn("gap-2 transition-all duration-200", isIconButton && "relative", className)}
        {...props}
      >
        {!isIconButton && label && (
          <span className="inline-flex shrink-0 items-center justify-start">{label}</span>
        )}

        <div
          className={cn(
            "relative box-content flex shrink-0 items-center justify-center overflow-hidden transition-all duration-300",
            isIconButton ? "absolute inset-0 rounded-sm border-0" : "h-5 w-24 rounded-sm border",
            isRecording
              ? "bg-primary/10"
              : isIconButton
                ? "bg-muted/50 border-0"
                : "border-border bg-muted/50",
            waveformClassName,
          )}
        >
          {shouldShowWaveform && (
            <LiveWaveform
              active={isRecording}
              processing={isProcessing || isSuccess}
              barWidth={2}
              barGap={1}
              barRadius={4}
              fadeEdges={false}
              sensitivity={1.8}
              smoothingTimeConstant={0.85}
              height={20}
              mode="static"
              className="animate-in fade-in absolute inset-0 h-full w-full duration-300"
            />
          )}

          {shouldShowTrailing && (
            <div className="animate-in fade-in absolute inset-0 flex items-center justify-center duration-300">
              {typeof trailing === "string" ? (
                <span className="text-muted-foreground px-1.5 font-mono text-xs font-medium select-none">
                  {trailing}
                </span>
              ) : (
                trailing
              )}
            </div>
          )}

          {!shouldShowWaveform && !shouldShowTrailing && icon && isIconButton && (
            <div className="animate-in fade-in absolute inset-0 flex items-center justify-center duration-300">
              {icon}
            </div>
          )}

          {isSuccess && showFeedback && (
            <div className="animate-in fade-in bg-background/80 absolute inset-0 flex items-center justify-center duration-300">
              <span className="text-primary">
                <Check className="size-3.5" />
              </span>
            </div>
          )}

          {isError && showFeedback && (
            <div className="animate-in fade-in bg-background/80 absolute inset-0 flex items-center justify-center duration-300">
              <span className="text-icon-danger">
                <X className="size-3.5" />
              </span>
            </div>
          )}
        </div>
      </Button>
    );
  },
);

VoiceButton.displayName = "VoiceButton";
